import React, { useState } from 'react';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import SmallResourcePreview from 'components/Utils/SmallResourcePreview';
import PatientBadge from 'components/Patient/PatientBadge';
import DefaultTextField from 'components/Utils/DefaultTextField';
import CalendarUtils from 'services/CalendarUtils';
import { StaffBadge } from 'services/StaffUtils';
import './style.scss';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DocumentUtils from 'services/DocumentUtils';
import LargeResourcePreview from 'components/Utils/LargeResourcePreview';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';

const dateFormat = 'Do MMM YYYY h:mm A';

// Outbound-attempt statuses pushed onto status_history by sendFileFax/resendFax.
// On the inbound side ('Received'), this list never matches, so getLastSentDate
// returns undefined and the derived field is hidden — the (N attempts) phrasing
// only makes sense for outbound faxes anyway.
const SEND_STATUSES = ['Submitted', 'Sending', 'Queued'];

const getLastSentDate = (fax) => {
  const history = fax?.status_history || [];
  // status_history is stored newest-first, so .find returns the most recent send.
  const lastSend = history.find((h) => SEND_STATUSES.includes(h?.status));
  return lastSend?.date;
};

const getOriginalCreationDate = (fax) => {
  const history = fax?.status_history || [];
  // For legacy rows (pre-fix), fax.date was mutated forward by every callback.
  // The earliest send entry in history is the true creation timestamp; .findLast
  // returns it because the array is newest-first.
  const firstSend = history.findLast?.((h) => SEND_STATUSES.includes(h?.status));
  return firstSend?.date ?? fax?.date;
};

const FaxDetailsView = (props) => {
  const dispatch = useAppDispatch();
  const [showResend, setShowResend] = useState(false);
  const lastSentDate = getLastSentDate(props.fax);
  const createdDate = getOriginalCreationDate(props.fax);
  const showLastSent = !!lastSentDate && lastSentDate !== createdDate;
  const attempts = props.fax.attempts || 1;
  return (
    <div className="fax-details read-only">
      {showResend && (
        <ConfirmationDialog
          title="Resend Fax"
          open={showResend}
          message="Confirm fax number to send to."
          onConfirm={(event, values) => {
            props.onResend(values?.['Number'], values?.['Comments']);
            setShowResend(false);
          }}
          form={[
            { type: 'textField', label: 'Number', value: props.fax?.to },
            { type: 'textField', label: 'Comments', multiline: true, value: props.fax?.comments },
          ]}
          onCancel={() => setShowResend(false)}
          loadingState={false}
        />
      )}
      <div className="fax-content">
        <div className="row info-wrapper">
          <div className="document-header">
            <TextField {...props} name="title" label="Title" value={props.fax.title} />
            <div className="row">
              <TextField name="status" label="Status" value={props.fax.status} />
              <TextField
                name="date"
                label={showLastSent ? 'Created' : 'Date'}
                value={CalendarUtils.format(createdDate, dateFormat)}
                style={{ width: '120%' }}
              />
            </div>
            {showLastSent && (
              <div className="row">
                <TextField
                  name="last_sent"
                  label={`Last sent (${attempts} attempt${attempts === 1 ? '' : 's'})`}
                  value={CalendarUtils.format(lastSentDate, dateFormat)}
                  style={{ width: '100%' }}
                />
              </div>
            )}
            <div className="row">
              <TextField name="to" label="Sent To" value={props.fax.to} />
              <TextField
                name="provider"
                label="Fax Provider Used"
                value={props.fax.provider}
                style={{ width: '120%' }}
              />
            </div>
            <span className="small-title mt-2">Sent by</span>
            <StaffBadge user={props.staff} />
            <span className="small-title">Patient</span>
            <PatientBadge patient={props.patient} goToProfile={true} />

            <div className="row" style={{ margin: '5px 0', padding: '5px 0' }}>
              {(props.fax.status === DocumentUtils.FAX_STATUS.failed ||
                props.fax.status === DocumentUtils.FAX_STATUS.submitted ||
                props.fax.status === DocumentUtils.FAX_STATUS.queued) && (
                <DefaultButton
                  className="red"
                  name="Archive Fax"
                  onClick={props.onArchive}
                  loading={props.archiving}
                  style={{ marginRight: '10%', width: '45%' }}
                  hide={!!props.fax.archived}
                />
              )}
              <DefaultButton
                name="Re-send Fax"
                onClick={() => setShowResend(true)}
                loading={props.resending}
                style={{ width: '45%' }}
              />
            </div>

            <ResourcePreview {...props} />

            <span className="small-title">Status History</span>
            <StatusHistory statusHistory={props.fax.status_history} />
            {!!props.fax.comments && (
              <>
                <span className="small-title">Comments</span>
                <TextareaAutosize name="comments" value={props.fax.comments} disabled={true} />
              </>
            )}
          </div>
        </div>
        {props.allowExpand === true && (
          <div className="row preview-wrapper">
            <LargeResourcePreview
              autoLoad={props.autoLoad}
              getFile={async (dispatchOptions) =>
                dispatch(
                  api.Resource.getResource({ resource: { url: props.fax.filename } }),
                  dispatchOptions,
                )
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

const TextField = ({ name, label, value, style }) => (
  <DefaultTextField name={name} label={label} value={value || ''} disabled={true} style={style} />
);

const ResourcePreview = ({ fax, patient }) => {
  const dispatch = useAppDispatch();

  return (
    <div className="row">
      <SmallResourcePreview
        patient={patient}
        title={fax.title}
        url={fax.filename}
        getFile={async (dispatchOptions) =>
          dispatch(api.Resource.getResource({ resource: { url: fax.filename } }), dispatchOptions)
        }
        showFax={true}
      />
    </div>
  );
};

const STATUS_COLORS = {
  Sent: '#70AE6E',
  Received: '#70AE6E',
  SendingFailed: '#ff3b5e',
  Queued: '#FFC857',
  Submitted: '#FFC857',
  Sending: '#42bcdd',
};

const COLLAPSE_THRESHOLD = 5;

const StatusHistoryItem = ({ item, showLine }) => (
  <div className="fax-status-history__item">
    <div className="fax-status-history__track">
      <div
        className="fax-status-history__dot"
        style={{ background: STATUS_COLORS[item.status] || '#838395' }}
      />
      {showLine && <div className="fax-status-history__line" />}
    </div>
    <div className="fax-status-history__content">
      <div className="fax-status-history__header">
        <span className="fax-status-history__status">
          {item.status === 'SendingFailed' ? 'Failed' : item.status}
        </span>
        <span className="fax-status-history__date">
          {CalendarUtils.format(item.date, dateFormat)}
        </span>
      </div>
      {!!item.details && <p className="fax-status-history__details">{item.details}</p>}
    </div>
  </div>
);

const StatusHistory = ({ statusHistory }) => {
  const [expanded, setExpanded] = useState(false);

  if (!statusHistory?.length) return null;

  const shouldCollapse = statusHistory.length >= COLLAPSE_THRESHOLD;
  // When collapsed: show only the latest entry (first item), hide the rest (including the always-"Submitted" last entry)
  const visibleItems = shouldCollapse && !expanded ? [statusHistory[0]] : statusHistory;

  return (
    <div className="fax-status-history">
      {visibleItems.map((item, index) => (
        <StatusHistoryItem
          key={index}
          item={item}
          showLine={index < visibleItems.length - 1 || (shouldCollapse && !expanded)}
        />
      ))}
      {shouldCollapse && !expanded && (
        <div className="fax-status-history__toggle" onClick={() => setExpanded(true)}>
          Show {statusHistory.length - 1} more
        </div>
      )}
      {shouldCollapse && expanded && (
        <div className="fax-status-history__toggle" onClick={() => setExpanded(false)}>
          Show less
        </div>
      )}
    </div>
  );
};

export default FaxDetailsView;
